import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getActiveAnnouncements } from "../services/announcementService";
import { getBuildData } from "../utils/buildDataLoader";
import "./AnnouncementBar.css";

export default function AnnouncementBar() {
  const CACHE_KEY = 'homepage_announcements_cache';
  const CACHE_TTL_MS = 5 * 60 * 1000;

  const readCache = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.timestamp || !Array.isArray(parsed.data)) return null;
      if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  };

  const writeCache = (data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore cache errors
    }
  };

  // OPTIMIZED: Try build-time data or cache first for instant loading
  const buildData = getBuildData();
  const cachedAnnouncements = readCache();
  const initialAnnouncements = buildData?.announcements || cachedAnnouncements || [];
  
  const [announcements, setAnnouncements] = useState(initialAnnouncements);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [loading, setLoading] = useState(initialAnnouncements.length === 0);

  useEffect(() => {
    const hasInitialData = announcements.length > 0;
    if (hasInitialData) {
      setLoading(false);
    } else {
      setLoading(true);
    }
    
    let isMounted = true;
    const fetchAnnouncements = async () => {
      try {
        const activeAnnouncements = await getActiveAnnouncements({ limitTo: 1 });
        if (!isMounted) return;
        if (activeAnnouncements.length > 0 || !hasInitialData) {
          setAnnouncements(activeAnnouncements);
          writeCache(activeAnnouncements);
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch announcements:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAnnouncements();

    return () => {
      isMounted = false;
    };
  }, []);

  // Rotate through announcements every 10 seconds
  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentAnnouncementIndex((prevIndex) => 
          (prevIndex + 1) % announcements.length
        );
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  // REMOVED: Hardcoded default message to prevent showing old promotional content
  // Only show real announcements from Firestore, no fallback defaults

  const getCurrentMessage = () => {
    // Show data immediately when available (cache-first loading provides instant data)
    // Don't wait for server sync - cached data is valid
    if (loading && announcements.length === 0) {
      return null; // Return null only if still loading AND no data yet
    }

    // If no announcements after loading, return null (bar stays visible but empty)
    if (announcements.length === 0) {
      return null;
    }

    const currentAnnouncement = announcements[currentAnnouncementIndex];
    
    if (!currentAnnouncement) {
      return null;
    }
    return (
      <>
        {currentAnnouncement.title && (
          <>
            {currentAnnouncement.title} —{" "}
          </>
        )}
        {currentAnnouncement.message}
        {currentAnnouncement.linkText && (
          <>
            {" "}
            {currentAnnouncement.linkUrl ? (
              <Link 
                to={currentAnnouncement.linkUrl} 
                className="announce-link"
                style={{ color: currentAnnouncement.accentColor }}
              >
                {currentAnnouncement.linkText}
              </Link>
            ) : (
              <span 
                className="announce-text-highlight"
                style={{ color: currentAnnouncement.accentColor, fontWeight: 'bold' }}
              >
                {currentAnnouncement.linkText}
              </span>
            )}
          </>
        )}
      </>
    );
  };

  const getCurrentStyles = () => {
    // Only apply custom styles if we have real announcements
    if (loading && announcements.length === 0) {
      return {}; // Use default styles from CSS while loading
    }
    
    if (announcements.length === 0) {
      return {}; // Use default styles if no announcements
    }

    const currentAnnouncement = announcements[currentAnnouncementIndex];
    if (!currentAnnouncement) {
      return {};
    }

    return {
      '--announce-bg': currentAnnouncement.backgroundColor || '#8e1111',
      '--announce-fg': currentAnnouncement.textColor || '#ffffff',
      '--announce-accent': currentAnnouncement.accentColor || '#ffe9a9'
    };
  };

  const currentMessage = getCurrentMessage();
  
  // Keep the bar structure visible always, but content may be empty
  return (
    <div 
      className="announce" 
      role="region" 
      aria-label="Site announcement"
      style={{
        ...getCurrentStyles(),
        minHeight: '50px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#8e1111',
        color: '#ffffff',
        position: 'sticky',
        top: '0',
        left: '0',
        right: '0',
        width: '100%',
        zIndex: '100'
      }}
    >
      <div className="announce-viewport">
        {/* Two equal sequences -> seamless loop */}
        <div className="announce-track">
          <div className="announce-seq">
            <span className="announce-text">{currentMessage || ''}</span>
          </div>
          <div className="announce-seq" aria-hidden="true">
            <span className="announce-text">{currentMessage || ''}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
